import { capitalize } from 'lodash'
import { SignalVerdict, SignalStrength, signalStrengthList, UniqueSignalVerdict, VerdictColorVariant } from '../types'

/**
 * Get verdicts that describe more or less significant threat level
 */
export function getThreatVerdicts(): SignalVerdict[] {
  return [SignalVerdict.Suspicious, SignalVerdict.Likely_malicious, SignalVerdict.Malicious]
}

/**
 * Get verdicts that describe no threat found
 */
export function getNonThreatVerdicts(): string[] {
  return [SignalVerdict.Benign, SignalVerdict.No_threat, SignalVerdict.Unknown]
}

export function getVerdictLabel<T extends string>(
  name: SignalVerdict | T,
  useHumanFriendlyVerdicts: boolean,
  opts?: { custom?: Record<T, string>; capped?: 'all' | 'first-char' | 'none' }
): string {
  const friendlyVerdictLabel = {
    [SignalVerdict.Unknown]: 'undetermined',
    [SignalVerdict.Benign]: 'trusted',
    [SignalVerdict.Informational]: 'no_threat_detected', // BC
    [SignalVerdict.No_threat]: 'no_threat_detected',
    [SignalVerdict.Suspicious]: 'low_risk',
    [SignalVerdict.Likely_malicious]: 'high_risk',
    [SignalVerdict.Malicious]: 'confirmed_threat',
    [SignalVerdict.System_error]: 'system_error',
  }

  const { custom, capped = 'all' } = opts ?? {}
  let label: string = custom?.[name as T] ?? name

  if (useHumanFriendlyVerdicts) {
    label = friendlyVerdictLabel[label as SignalVerdict] ?? label
  } else if (label === SignalVerdict.Informational) {
    label = SignalVerdict.No_threat
  }

  const words = label.split('_')

  switch (capped) {
    case 'all':
      return words.map((word) => capitalize(word)).join(' ')
    case 'first-char':
      return capitalize(words.join(' '))
    case 'none':
    default:
      return words.join(' ')
  }
}

/**
 * Cast numeric strength value to verdict, in case when we know that it exactly matches verdict
 */
export function castStrengthToVerdict(strength: number, mergeSimilar = false): SignalVerdict {
  if (strength < SignalStrength.NoImpact) {
    return SignalVerdict.Benign
  } else if (strength < SignalStrength.VeryLowImpact) {
    return SignalVerdict.Unknown
  } else if (strength <= SignalStrength.LowImpact) {
    return SignalVerdict.No_threat
  } else if (strength <= SignalStrength.ConsiderableImpact) {
    return SignalVerdict.Suspicious
  } else if (strength <= SignalStrength.SevereImpact && !mergeSimilar) {
    return SignalVerdict.Likely_malicious
  } else {
    return SignalVerdict.Malicious
  }
}

/**
 * Cast string verdict description to numeric signal strength value
 */
export function castVerdictToStrength(verdict: SignalVerdict | string): SignalStrength {
  switch (verdict) {
    case SignalVerdict.Benign:
      return SignalStrength.Whitelisted
    case SignalVerdict.Informational: // BC
    case SignalVerdict.No_threat:
      return SignalStrength.LowImpact
    case SignalVerdict.Suspicious:
      return SignalStrength.ConsiderableImpact
    case SignalVerdict.Likely_malicious:
      return SignalStrength.SevereImpact
    case SignalVerdict.Malicious:
      return SignalStrength.CriticalImpact
    case SignalVerdict.Unknown:
    default:
      return SignalStrength.NoImpact
  }
}

// Return all verdicts we use, except for those that are kept now only for BC
export function uniqueVerdicts() {
  const verdicts: { [key: string]: string } = { ...SignalVerdict }
  delete verdicts.Informational
  delete verdicts.System_error

  return verdicts as unknown as UniqueSignalVerdict
}

// Get verdicts ordered by strength
export function getOrderedVerdicts(): SignalVerdict[] {
  const result: SignalVerdict[] = []

  for (let i = 0; i < signalStrengthList.length; i++) {
    const value = signalStrengthList[i]
    const verdict = castStrengthToVerdict(value)

    !result.includes(verdict) && result.push(verdict)
  }

  return result.reverse()
}

export function getOrderedThreatVerdicts(): SignalVerdict[] {
  const threatVerdicts = getThreatVerdicts()
  return getOrderedVerdicts().filter((verdict) => threatVerdicts.includes(verdict))
}

/**
 * Compare two verdicts and return the one with higher severity.
 * Uses signal strength as the comparison metric.
 */
export function getHigherSeverityVerdict(a: SignalVerdict, b: SignalVerdict): SignalVerdict {
  return castVerdictToStrength(a) >= castVerdictToStrength(b) ? a : b
}

export function getSignalVerdictIcon(verdict: VerdictColorVariant) {
  const defaultVerdictIcons = {
    [SignalVerdict.Malicious]: 'tick',
    [SignalVerdict.Likely_malicious]: 'tick',
    [SignalVerdict.Suspicious]: 'warning-triangle',
    [SignalVerdict.No_threat]: 'check-circle',
    [SignalVerdict.Informational]: 'check-circle',
    [SignalVerdict.Benign]: 'shield-ok',
    [SignalVerdict.Unknown]: 'question-circle',
    [SignalVerdict.System_error]: 'block-circle',
    error: 'block-circle',
    neutral: 'information',
  }

  return defaultVerdictIcons[verdict]
}
